/* eslint-disable eqeqeq */
import React, { useCallback, useEffect, useState } from "react";
import {
  API,
  defaultLimitPagination,
  get,
  QuestionRiskOptions,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../../../config";
import { useLocation, useNavigate } from "react-router-dom";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../../../../components/Pagination";
import MainBody from "../../../../components/MainBody/MainBody";
import sortCaret from "../../../../components/SortCaret";
import { toast } from "react-toastify";
import { ResponsiveDrawerForm } from "../../../../components/ResponsiveDrawerForm";
import { LucidePlusCircle } from "lucide-react";
import AddEditRiskAssessment from "./AddEditRiskAssessment";
import { useOrganization } from "../../../../lib/context/OrganisationContext";
import RiskAssementActionButton from "./RiskAssementActionButton";
import { debounce } from "lodash";
import UnauthorisedAccess from "../../../../UnauthorisedAccess";

const RiskAssementList = ({ sectionType, readAccess, writeAccess }) => {
  const navigate = useNavigate();
  const { organizationData } = useOrganization();

  // * URL State
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const search = query.get("search");
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");

  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();

  // * Local State
  const [ctfList, setCTFList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [searchParam, setSearchParam] = useState(search || "");
  const [searchParamData, setSearchParamData] = useState(search || "");
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();

  const [riskId, setRiskId] = useState(null);
  const [showAddEditRisk, setShowAddEditRisk] = useState(false);
  const [edit, setEdit] = useState(false);

  const toggleShowAddEditRisk = () => {
    if (showAddEditRisk) {
      setEdit(false);
      setRiskId(null);
    }
    setShowAddEditRisk(!showAddEditRisk);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    [],
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    if (nextValue?.length >= 2 || nextValue?.length === 0) {
      debouncedSave(nextValue);
    }
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  // * Fetch CTF List
  const fetchRisks = useCallback(
    async (passLimit, passPage) => {
      if (organizationData?.organizationId) {
        try {
          setLoading(true);
          const { data } = await get(
            `${API.GET_RISK_ASSESSMENT}?limit=${
              passLimit ? passLimit : limit
            }&search=${searchParam ? searchParam : ""}&page=${
              passPage ? passPage : page
            }&section_type=${sectionType}&organization_id=${
              organizationData?.organizationId
                ? organizationData?.organizationId
                : ""
            }&sort_column=${sortField ? sortField : ""}&sort_order=${
              sortOrder ? sortOrder : ""
            }`,
          );
          setLoading(false);
          setLimit(data?.data?.per_page);
          setPage(data?.data?.current_page);
          setTotal(data?.data?.total);
          setFrom(data?.data?.from);
          setTo(data?.data?.to);
          setCTFList(data?.data?.data || []);
        } catch (e) {
          setLoading(false);
          setCTFList([]);
          toast.error("Failed to load CTFs");
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      organizationData?.organizationId,
      sectionType,
      sortOrder,
      sortField,
      searchParam,
    ],
  );

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC",
      );
    }
  };

  useEffect(() => {
    fetchRisks();
  }, [fetchRisks]);

  // * Table Row Generator
  const riskGenerator = (list) =>
    list.map((item) => ({
      key: item?.id,
      year: item?.year || "",
      version: item?.version ? `Y${item?.year} (V${item?.version})` : "",
      risk: item?.risk
        ? QuestionRiskOptions?.find((qItem) => qItem.value == item?.risk)?.label
        : "",
      risk_action: item?.risk_action || "",
      assessment_date: item?.assessment_date || "",
      assessed_by: item?.user
        ? `${item?.user?.first_name || ""} ${item?.user?.last_name || ""}`
        : "",
      next_review_date: item?.next_review_date || "",
      compliance_manager: item?.compliance_manager
        ? `${item?.compliance_manager?.first_name || ""} ${
            item?.compliance_manager?.last_name || ""
          }`
        : "",
      // approved_date: item?.approved_date || "",
      // approved_by: item?.approved_by
      //   ? `${item?.approved_by?.first_name || ""} ${
      //       item?.approved_by?.last_name || ""
      //     }`
      //   : "",
      action: (
        <RiskAssementActionButton
          id={item?.id}
          setEdit={setEdit}
          setRiskId={setRiskId}
          toggleShowAddEditRisk={toggleShowAddEditRisk}
          incident={item}
          sectionType={sectionType}
          approved={item?.approved}
          fetchRisks={() => fetchRisks()}
          setLoading={setLoading}
          approvalPremission={
            item?.approval_permission ? item?.approval_permission : ""
          }
        />
      ),
    }));

  const riskData = riskGenerator(ctfList);

  // * Columns
  const columns = [
    {
      dataField: "year",
      text: "Year",
      sort: true,
      sortCaret,
      headerClasses: THClasses,
      classes: TDClasses,
    },
    {
      dataField: "version",
      text: "Version",
      headerClasses: THClasses,
      classes: TDClasses,
    },
    {
      dataField: "risk",
      text: "Risk Level",
      headerClasses: THClasses,
      classes: TDClasses,
    },
    {
      dataField: "risk_action",
      text: "Risk Action",
      headerClasses: THClasses,
      classes: TDClasses,
    },
    {
      dataField: "assessment_date",
      text: "Assessment Date",
      sort: true,
      sortCaret,
      headerClasses: THClasses,
      classes: TDClasses,
    },
    {
      dataField: "assessed_by",
      text: "Assessed By",
      headerClasses: THClasses,
      classes: TDClasses,
    },
    {
      dataField: "next_review_date",
      text: "Next Review Date",
      sort: true,
      sortCaret,
      headerClasses: THClasses,
      classes: TDClasses,
    },
    {
      dataField: "compliance_manager",
      text: "Compliance Officer",
      headerClasses: THClasses,
      classes: TDClasses,
    },
    // {
    //   dataField: "approved_date",
    //   text: "Approved Date",
    //   sort: true,
    //   sortCaret,
    //   headerClasses: THClasses,
    //   classes: TDClasses,
    // },
    // {
    //   dataField: "approved_by",
    //   text: "Approved By",
    //   headerClasses: THClasses,
    //   classes: TDClasses,
    // },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "action",
      text: "Action",
      align: "center",
      headerAlign: "center",
      style: { width: "127px" },
    },
  ];

  // * Pagination Handlers
  const handleUpdateLimit = (e) => {
    const newLimit = e.target.value;
    setLimit(newLimit);
    setPage(1);
    fetchRisks(newLimit, 1);
  };

  const handleChangePage = (p) => {
    setPage(p);
    fetchRisks(limit, p);
  };

  useEffect(() => {
    navigate(
      `?tab=program&childTab=risk-assessment&subChildTab=${
        sectionType === "AML" ? "aml" : "ctf"
      }&limit=${limit ? limit : defaultLimitPagination}&page=${
        page ? page : 1
      }`,
      {
        replace: true,
      },
    );
  }, [navigate, limit, page, sectionType]);

  return readAccess ? (
    <MainBody
      loading={loading}
      innerBody={true}
      parentHeight={true}
      searchParamData={searchParamData}
      handleClearSearch={handleClearSearch}
      handleSearchChange={handleSearchChange}
      actionContent={
        <>
          {/* Add Customer */}
          <ResponsiveDrawerForm
            description={""}
            title={edit ? "Edit Risk Assessment" : "Add Assessment"}
            handleOpen={() => {}}
            popupType={"risk-assessments"}
            handleClose={() => {
              setEdit(false);
              setRiskId(null);
              setShowAddEditRisk(false);
            }}
            buttonText={
              <div className="flex items-center gap-1">
                <LucidePlusCircle size={18} />
                <span>
                  Conduct{" "}
                  {sectionType === "AML"
                    ? "ML"
                    : sectionType === "CTF"
                      ? "TF/PF"
                      : sectionType}{" "}
                  Risk Assessment
                </span>
              </div>
            }
            className={"overflow-hidden"}
            open={showAddEditRisk}
            buttonClassName={""}
            setOpen={setShowAddEditRisk}
            write={true}
          >
            <AddEditRiskAssessment
              edit={edit}
              toggleShowAddEditRisk={toggleShowAddEditRisk}
              id={riskId}
              sectionType={sectionType}
              organizationId={organizationData?.organizationId}
              fetchRiskData={() => fetchRisks(limit, page)}
            />
          </ResponsiveDrawerForm>
        </>
      }
    >
      <div className="custom-table">
        <div className="table-body-class">
          <BootstrapTable
            keyField="key"
            remote
            data={riskData}
            columns={columns}
            noDataIndication="No Data Found"
            rowClasses={TRClasses}
            classes={TableClasses}
            onTableChange={handleTableChange}
            wrapperClasses={TableWrapClasses}
          />
        </div>

        <Pagination
          total={total}
          limit={parseInt(limit)}
          currentPage={page}
          updateLimit={handleUpdateLimit}
          updatePage={handleChangePage}
          from={from}
          to={to}
        />
      </div>
    </MainBody>
  ) : (
    <UnauthorisedAccess expandList={true} popup={true} />
  );
};

export default RiskAssementList;
